import axios from 'axios';

export class BlobDownloadError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(code);
    this.name = 'BlobDownloadError';
  }
}

const parseFileName = (contentDisposition: string, fallbackName: string): string => {
  const utf8 = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      // fall through to the plain name
    }
  }
  const plain = contentDisposition.match(/filename="?([^";]+)"?/i);
  return plain?.[1] || fallbackName;
};

const readErrorCode = async (data: unknown, status: number): Promise<string> => {
  try {
    const text = data instanceof Blob ? await data.text() : typeof data === 'string' ? data : JSON.stringify(data);
    const parsed = JSON.parse(text);
    if (typeof parsed?.message === 'string') return parsed.message;
  } catch {
    // not JSON
  }
  return `HTTP_${status}`;
};

/**
 * Downloads an authenticated binary through the API and hands it to the browser.
 *
 * - 401 is left to axios so the global interceptor logs the user out as usual.
 * - Every other error status resolves here: with `responseType: 'blob'` the body
 *   is a Blob and the interceptor would only show "An unexpected error occurred";
 *   the JSON error is read back and thrown as {@link BlobDownloadError} so the
 *   caller shows a single, translated toast.
 * - The object URL is revoked after a delay: revoking right after `click()` can
 *   interrupt the native PDF viewer / share sheet on iOS Safari.
 */
export async function downloadBlob(url: string, fallbackName: string): Promise<void> {
  const response = await fetchBlob(url);
  const blob = response.data as Blob;
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = parseFileName(String(response.headers?.['content-disposition'] || ''), fallbackName);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
}

/** Desktop only: shows the file in a new tab (PDF viewer) instead of saving it. */
export async function openBlobInNewTab(url: string): Promise<void> {
  const response = await fetchBlob(url);
  const objectUrl = window.URL.createObjectURL(response.data as Blob);
  const opened = window.open(objectUrl, '_blank', 'noopener');
  if (!opened) {
    window.location.assign(objectUrl);
  }
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
}

async function fetchBlob(url: string) {
  const response = await axios.get(url, {
    responseType: 'blob',
    validateStatus: (status) => status !== 401,
  });
  if (response.status >= 400) {
    throw new BlobDownloadError(response.status, await readErrorCode(response.data, response.status));
  }
  return response;
}
